import { Context, createContext, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { useUpdatePendingChanges } from "./pendingChangesContext";

export interface EditableItemContextType<T> {
	/** Item being currently edited */
	item?: T;
	/** Record of item before last save */
	savedItem?: T;
	/** If true, item has been edited */
	pendingChanges?: boolean;
	/** If true, item has been loaded from server, or for the first time */
	itemLoaded?: boolean;
	setItemLoaded: (itemLoaded?: boolean) => void;
	/**
	 * Resets to unchanged item of this
	 * Best used right after load or save
	 */
	overwriteItem: (item: T) => void;
	/** Updates item (partial) without setting as having been changed */
	updateItem: (item?: Partial<T>) => void;
	/** Updates record of saved item (partial) */
	updateSavedItem: (item?: Partial<T>) => void;
	/** Updates item (partial), and marks as having been changed */
	editItem: (item?: Partial<T>) => void;
	/** Deletes record of item and saved item */
	clearItem: () => void;
	/** Clears all changes by overwriting "item" with "savedItem" */
	clearChanges: () => void;
	/** Marks as having been changed */
	setHasPendingChanges: (hasPendingChanges?: boolean) => void;
}

const defaultContextValues = {
	item: undefined,
	savedItem: undefined,
	pendingChanges: false,
	itemLoaded: false,
	setItemLoaded: () => undefined,
	editItem: () => undefined,
	overwriteItem: () => undefined,
	updateItem: () => undefined,
	updateSavedItem: () => undefined,
	clearItem: () => {},
	clearChanges: () => {},
	setHasPendingChanges: () => {},
} as EditableItemContextType<any>;

export function getEditableItemContext<T = any>(contextValues?: EditableItemContextType<T>) {
	return createContext<EditableItemContextType<T>>(contextValues ?? defaultContextValues);
}

export interface EditableItemProviderProps<T> {
	item?: Partial<T>;
	children: ReactNode;
}

/** `type` is the key this item's pending changes are tracked under in PendingChangesProvider */
export function EditableItemProvider<T>(Context: Context<any>, props: EditableItemProviderProps<T>, type?: unknown) {
	const { children } = props;

	const [item, setItem] = useState<T>(props.item as T);
	const [savedItem, setSavedItem] = useState<T>();
	const [itemLoaded, setItemLoaded] = useState(false);
	const [pendingChanges, setPendingChanges] = useState(false);
	const setItemChanged = useUpdatePendingChanges(type);

	useEffect(() => {
		setItemChanged(pendingChanges);
		return () => setItemChanged(false);
	}, [pendingChanges, setItemChanged]);

	const updateItem = useCallback((newItem?: Partial<T>) => setItem((prev) => ({ ...prev, ...newItem })), []);
	const updateSavedItem = useCallback(
		(newItem?: Partial<T>) => setSavedItem((prev) => ({ ...prev, ...newItem })),
		[],
	);
	const editItem = useCallback(
		(newItem?: Partial<T>) => {
			updateItem(newItem);
			setPendingChanges(true);
		},
		[updateItem],
	);
	const overwriteItem = useCallback((newItem: T) => {
		setItem(newItem);
		setSavedItem(newItem);
		setPendingChanges(false);
	}, []);
	const clearItem = useCallback(() => overwriteItem(null), [overwriteItem]);
	const clearChanges = useCallback(() => overwriteItem(savedItem), [overwriteItem, savedItem]);
	const setHasPendingChanges = useCallback((hasPendingChanges = true) => setPendingChanges(hasPendingChanges), []);

	const value = useMemo(
		() => ({
			item,
			savedItem,
			clearChanges,
			clearItem,
			editItem,
			updateItem,
			updateSavedItem,
			overwriteItem,
			setHasPendingChanges,
			pendingChanges,
			itemLoaded,
			setItemLoaded,
		}),
		[
			item,
			savedItem,
			clearChanges,
			clearItem,
			editItem,
			updateItem,
			updateSavedItem,
			overwriteItem,
			setHasPendingChanges,
			pendingChanges,
			itemLoaded,
		],
	);

	return <Context.Provider value={value}>{children}</Context.Provider>;
}
